// The per-machine verdict the Fleet page shows (#6557, owner rulings G3/G8/G14 on #6552):
// one status, one health, three signals and a needs-attention flag, computed by ONE pure
// function. `GET /v1/fleet/utilization` (`apps/api`) and MCP `box fleet_status`
// (`packages/mcp`) both call {@link machineVerdict}; neither restates a threshold.
//
// TWO DIAGNOSES, ON PURPOSE. `boxHealth` (`packages/mcp/src/army/box.ts`, published as
// `RunnerHealth` on every roster row, 7-minute `BOX_STALE_AFTER_MS`) answers "what should
// an agent DO about this box" for the roster and MCP liveness contract. This verdict
// answers "how is this machine doing" for the Fleet page, on the check-in clock G14 sets.
// They read different clocks and are allowed to differ; neither replaces the other.
//
// Every threshold is named once. Three of them restate a constant that lives where this
// package may not import (domain imports nothing internal):
//   - {@link MACHINE_NOT_RESPONDING_AFTER_MS} = `PRESENCE_TTL_MS` (`@developerz/nats`)
//   - {@link MACHINE_OFFLINE_AFTER_MS} = `OFFLINE_ALERT_THRESHOLD_MS` (`apps/worker`
//     `cron/runner-sweep.ts`)
//   - `MACHINE_JOB_STUCK_AFTER_MS` (`./machine-jobs-verdict.ts`, #6746) =
//     `ADAPTER_CEILING_MINUTES` (`apps/worker` `cron/stuck-task.ts`)
// and all three equalities are pinned by `apps/worker/src/cron/machine-verdict-thresholds.test.ts`.
// {@link BOX_FIRST_BEAT_GRACE_MS} MOVED here from `packages/mcp` and is re-exported there.
//
// THE JOBS STATE (#6746) rides beside the status, never inside it: `machineStatus` reads
// `working` / `idle` off the beat's `activeSessions`, so a machine running another
// account's job is still working, and `jobs` describes only the account's own jobs
// (`machineJobsVerdict`, `./machine-jobs-verdict.ts`).

import {
  type MachineJobInput,
  type MachineJobsVerdictState,
  machineJobsVerdict,
} from './machine-jobs-verdict';
import type { BoxStats } from './stats';

/**
 * How long after enrollment a box may go without its first heartbeat before it is
 * called broken rather than booting.
 *
 * Generous on purpose: the join script installs a whole toolchain (bun, git, gh,
 * docker, the coding agent) on a fresh VM before anything beats, so a box that is
 * merely SLOW must not read as wedged — that misread costs an operator a rebuild of
 * a box that was working. The cost of being late in the other direction is only that
 * the wait lasts a few minutes longer than it had to. Read by `boxHealth`
 * (`packages/mcp`, which re-exports it) and by {@link machineVerdict}'s `starting`.
 */
export const BOX_FIRST_BEAT_GRACE_MS = 10 * 60 * 1000;

/**
 * G14's lower edge: silent this long is not responding. It is the presence key's TTL,
 * so a machine whose key has LAPSED has been silent at least this long — the verdict
 * reads "key absent" as "past this edge" rather than comparing a clock to it, because
 * the durable `last_heartbeat_at` is re-stamped by the sweep's tick, not by the beat.
 */
export const MACHINE_NOT_RESPONDING_AFTER_MS = 90 * 1000;

/** G14's upper edge: silent this long (or longer) is offline. */
export const MACHINE_OFFLINE_AFTER_MS = 30 * 60 * 1000;

/** CPU now (1-minute load / cores) at or above this is busy: the `warn` tier. */
export const MACHINE_CPU_BUSY_RATIO = 0.7;

/** G8: every sample of the window must be STRICTLY above this for `overloaded`. */
export const MACHINE_CPU_OVERLOAD_RATIO = 0.9;

/**
 * G8: how many one-minute samples make the overload window. Fewer never says overloaded
 * ({@link machineOverloaded} returns false under the count). It stops one or two hot
 * minutes (a build burst) from reading as a machine stuck at full load.
 */
export const MACHINE_OVERLOAD_WINDOW_SAMPLES = 5;

/** G8: a gap between two consecutive window samples longer than this breaks the window. */
export const MACHINE_OVERLOAD_MAX_GAP_MS = 2 * 60 * 1000;

/** G8: the newest window sample must be at most this old. */
export const MACHINE_OVERLOAD_FRESH_MS = 2 * 60 * 1000;

/** Memory used (1 - free / `specs.memBytes`) at or above this warns. */
export const MACHINE_MEMORY_WARN_RATIO = 0.75;

/** Memory used strictly above this is critical. */
export const MACHINE_MEMORY_CRIT_RATIO = 0.9;

/** Workspace disk used (1 - free / total) at or above this warns. */
export const MACHINE_DISK_WARN_RATIO = 0.8;

/** Workspace disk used strictly above this is critical. */
export const MACHINE_DISK_CRIT_RATIO = 0.9;

/**
 * Headroom over the box's own refusal floor: free workspace disk under `floor + this`
 * is critical, total or no total. A floor of `0` (the box refuses nothing) still leaves
 * this margin as the line — a box with under 5 GiB free cannot run a checkout either way.
 */
export const MACHINE_DISK_FLOOR_MARGIN_BYTES = 5 * 1024 * 1024 * 1024;

/** What the machine is doing, in the precedence {@link machineStatus} applies. */
export type MachineStatus =
  | 'unknown'
  | 'waiting_to_connect'
  | 'paused'
  | 'starting'
  | 'offline'
  | 'not_responding'
  | 'working'
  | 'idle';

/** The worst condition, in the order {@link machineHealth} folds (worst first, after `unknown`). */
export type MachineHealth =
  | 'unknown'
  | 'offline'
  | 'not_responding'
  | 'overloaded'
  | 'disk_critical'
  | 'memory_critical'
  | 'disk_warning'
  | 'memory_warning'
  | 'busy'
  | 'healthy';

/** One signal's tier. `not_reported` is a missing reading, never a zero one. */
export type MachineSignalLevel = 'ok' | 'warn' | 'crit' | 'not_reported';

/**
 * A graded reading. `ratio` is the used fraction the tier was read off (CPU: load /
 * cores, which may exceed 1), or null when there was none — including a disk graded by
 * the floor rule alone, which has no denominator.
 */
export type MachineSignal = { level: MachineSignalLevel; ratio: number | null };

export type MachineSignals = { cpu: MachineSignal; memory: MachineSignal; disk: MachineSignal };

/** One `runner_stat_samples` row as the verdict reads it. */
export type MachineSample = { sampledAtMs: number; loadAvg1: number; cpuCores: number | null };

/**
 * The presence read. `unknown` is a statement about the READ (the KV could not be
 * consulted). `ok` + `live: null` is a machine with no live key. A live key whose payload
 * did not decode is still live, with null fields.
 */
export type MachinePresence =
  | { kind: 'unknown' }
  | { kind: 'ok'; live: { activeSessions: number | null; stats: BoxStats | null } | null };

export type MachineVerdictInput = {
  nowMs: number;
  /** `pending` = a setup code was minted and nothing enrolled (expired-but-unreaped included). */
  enrollment: 'pending' | 'enrolled';
  /** An operator drain mark (`runners.drained_at`) is set. */
  drained: boolean;
  enrolledAtMs: number | null;
  createdAtMs: number;
  /** The durable check-in column (`runners.last_heartbeat_at`), null when it never beat. */
  lastHeartbeatAtMs: number | null;
  presence: MachinePresence;
  /** Enroll-time hardware; either half may be unknown. */
  specs: { cpuCores: number | null; memBytes: number | null } | null;
  /** The machine's newest samples, any order; only the newest {@link MACHINE_OVERLOAD_WINDOW_SAMPLES} count. */
  samples: readonly MachineSample[];
  /**
   * The jobs the machine holds for the viewing account (#6746), from `machineJobInputs`:
   * `[]` when it holds none and when it has no live check-in (its jobs are last known).
   */
  jobs: readonly MachineJobInput[];
};

/**
 * The verdict, never null. Discriminated on `status`: an unreadable presence store is the
 * one status whose health is `unknown`, and no other status may carry `unknown` health.
 */
export type MachineVerdict =
  | {
      status: 'unknown';
      health: 'unknown';
      signals: MachineSignals;
      needsAttention: boolean;
      /** The worst of the account's jobs on the machine (#6746), null when none counts. */
      jobs: MachineJobsVerdictState | null;
    }
  | {
      status: Exclude<MachineStatus, 'unknown'>;
      health: Exclude<MachineHealth, 'unknown'>;
      signals: MachineSignals;
      needsAttention: boolean;
      jobs: MachineJobsVerdictState | null;
    };

const NOT_REPORTED: MachineSignal = { level: 'not_reported', ratio: null };

/** When a machine that has never checked in stops being `starting`. */
export function machineFirstBeatDeadlineMs(input: {
  enrolledAtMs: number | null;
  createdAtMs: number;
}): number {
  return (input.enrolledAtMs ?? input.createdAtMs) + BOX_FIRST_BEAT_GRACE_MS;
}

/**
 * The status, in this precedence (each arm returns before the next is read):
 * `unknown` > `waiting_to_connect` > `paused` > `starting` > `offline` > `not_responding`
 * > `working` > `idle`. `unknown` never becomes `not_responding`: a store that could not
 * be read says nothing about the machine.
 */
export function machineStatus(input: MachineVerdictInput): MachineStatus {
  if (input.presence.kind === 'unknown') return 'unknown';
  if (input.enrollment === 'pending') return 'waiting_to_connect';
  if (input.drained) return 'paused';
  const live = input.presence.live;
  if (live !== null) return (live.activeSessions ?? 0) > 0 ? 'working' : 'idle';
  // No live key: silent for at least MACHINE_NOT_RESPONDING_AFTER_MS (the key's TTL).
  if (input.lastHeartbeatAtMs === null) {
    const deadline = machineFirstBeatDeadlineMs(input);
    if (input.nowMs < deadline) return 'starting';
    return input.nowMs - deadline >= MACHINE_OFFLINE_AFTER_MS ? 'offline' : 'not_responding';
  }
  return input.nowMs - input.lastHeartbeatAtMs >= MACHINE_OFFLINE_AFTER_MS
    ? 'offline'
    : 'not_responding';
}

/**
 * G8, decided server-side: the newest {@link MACHINE_OVERLOAD_WINDOW_SAMPLES} samples
 * each strictly above {@link MACHINE_CPU_OVERLOAD_RATIO}, consecutive gaps no longer than
 * {@link MACHINE_OVERLOAD_MAX_GAP_MS}, the newest within {@link MACHINE_OVERLOAD_FRESH_MS}
 * of now. A sample with no core count reads the enroll-time count; with neither it cannot
 * count, so the window fails.
 *
 * The five are DISTINCT minutes by construction, not by a check here: the samples come
 * from `runner_stat_samples`, whose primary key `runner_stat_samples_runner_sampled_pk`
 * on `(runner_id, sampled_at)` (the worker's truncated minute, `@developerz/db`
 * `schema/fleet.ts`) admits one row per machine per minute, so one minute cannot fill two
 * slots of the window.
 */
export function machineOverloaded(
  samples: readonly MachineSample[],
  specCores: number | null,
  nowMs: number,
): boolean {
  const window = [...samples]
    .sort((a, b) => b.sampledAtMs - a.sampledAtMs)
    .slice(0, MACHINE_OVERLOAD_WINDOW_SAMPLES);
  if (window.length < MACHINE_OVERLOAD_WINDOW_SAMPLES) return false;
  const newest = window[0];
  if (newest === undefined || nowMs - newest.sampledAtMs > MACHINE_OVERLOAD_FRESH_MS) return false;
  return window.every((s, i) => {
    const cores = s.cpuCores ?? specCores;
    if (cores === null || cores <= 0 || s.loadAvg1 / cores <= MACHINE_CPU_OVERLOAD_RATIO) {
      return false;
    }
    const prev = window[i - 1];
    return prev === undefined || prev.sampledAtMs - s.sampledAtMs <= MACHINE_OVERLOAD_MAX_GAP_MS;
  });
}

function cpuSignal(input: MachineVerdictInput, stats: BoxStats | null): MachineSignal {
  const cores = input.specs?.cpuCores ?? null;
  const ratio = stats !== null && cores !== null && cores > 0 ? stats.loadAvg1 / cores : null;
  if (machineOverloaded(input.samples, cores, input.nowMs)) return { level: 'crit', ratio };
  if (ratio === null) return NOT_REPORTED;
  return { level: ratio >= MACHINE_CPU_BUSY_RATIO ? 'warn' : 'ok', ratio };
}

function memorySignal(input: MachineVerdictInput, stats: BoxStats | null): MachineSignal {
  const total = input.specs?.memBytes ?? null;
  if (stats === null || total === null || total <= 0) return NOT_REPORTED;
  const ratio = 1 - stats.freeMemBytes / total;
  if (ratio > MACHINE_MEMORY_CRIT_RATIO) return { level: 'crit', ratio };
  return { level: ratio >= MACHINE_MEMORY_WARN_RATIO ? 'warn' : 'ok', ratio };
}

/**
 * The workspace disk. Under `floor + margin` free is critical whatever the total says;
 * with no total (a runner older than #6554) that floor rule is the ONLY rule, so there is
 * no warn tier and no ratio.
 */
function diskSignal(stats: BoxStats | null): MachineSignal {
  const ws = stats?.workspaceDisk;
  if (ws === undefined || ws.freeBytes === null) return NOT_REPORTED;
  const underFloor = ws.freeBytes < ws.floorBytes + MACHINE_DISK_FLOOR_MARGIN_BYTES;
  const total = ws.totalBytes ?? null;
  if (total === null || total <= 0) return { level: underFloor ? 'crit' : 'ok', ratio: null };
  const ratio = 1 - ws.freeBytes / total;
  if (underFloor || ratio > MACHINE_DISK_CRIT_RATIO) return { level: 'crit', ratio };
  return { level: ratio >= MACHINE_DISK_WARN_RATIO ? 'warn' : 'ok', ratio };
}

/** Every signal, read off the live beat (`not_reported` without one) and the samples. */
export function machineSignals(input: MachineVerdictInput): MachineSignals {
  const stats = input.presence.kind === 'ok' ? (input.presence.live?.stats ?? null) : null;
  return {
    cpu: cpuSignal(input, stats),
    memory: memorySignal(input, stats),
    disk: diskSignal(stats),
  };
}

/**
 * Worst wins: offline / not_responding (the status itself) > overloaded > disk critical >
 * memory critical > disk warning > memory warning > busy > healthy. Read for `working` and
 * `idle` only: the three NEUTRAL statuses ({@link MACHINE_NEUTRAL_STATUSES}) never reach it.
 */
function machineHealth(
  status: Exclude<MachineStatus, 'unknown'>,
  s: MachineSignals,
): Exclude<MachineHealth, 'unknown'> {
  if (status === 'offline' || status === 'not_responding') return status;
  if (s.cpu.level === 'crit') return 'overloaded';
  if (s.disk.level === 'crit') return 'disk_critical';
  if (s.memory.level === 'crit') return 'memory_critical';
  if (s.disk.level === 'warn') return 'disk_warning';
  if (s.memory.level === 'warn') return 'memory_warning';
  if (s.cpu.level === 'warn') return 'busy';
  return 'healthy';
}

/**
 * Owner ruling (note 4 on #6557): a machine that is not running work by design —
 * waiting to connect, paused by a drain, or still inside its first-beat grace — has
 * NEUTRAL health (`healthy`) and never needs attention, whatever its signals or hygiene
 * say. Its signals are still graded and published. A drained machine reads paused, the
 * existing "Drained, not Offline" precedent.
 */
export const MACHINE_NEUTRAL_STATUSES = ['waiting_to_connect', 'paused', 'starting'] as const;

function isNeutralStatus(
  status: MachineStatus,
): status is (typeof MACHINE_NEUTRAL_STATUSES)[number] {
  return (MACHINE_NEUTRAL_STATUSES as readonly MachineStatus[]).includes(status);
}

/**
 * The verdict. For `working` / `idle` / `offline` / `not_responding`, `needsAttention` is
 * health outside {healthy, busy}, a live beat reporting hygiene errors, or a stuck job
 * (#6746); `unknown` needs attention only on hygiene errors; the neutral statuses never do.
 * `jobs` is folded first and carried on every arm, neutral ones included.
 */
export function machineVerdict(input: MachineVerdictInput): MachineVerdict {
  const signals = machineSignals(input);
  const status = machineStatus(input);
  const jobs = machineJobsVerdict(input.jobs, input.nowMs);
  if (isNeutralStatus(status)) {
    return { status, health: 'healthy', signals, needsAttention: false, jobs };
  }
  const live = input.presence.kind === 'ok' ? input.presence.live : null;
  const hygieneErrors = (live?.stats?.hygiene?.errors ?? 0) > 0;
  if (status === 'unknown') {
    return { status, health: 'unknown', signals, needsAttention: hygieneErrors, jobs };
  }
  const health = machineHealth(status, signals);
  const calm = health === 'healthy' || health === 'busy';
  return {
    status,
    health,
    signals,
    needsAttention: !calm || hygieneErrors || jobs === 'stuck',
    jobs,
  };
}

/**
 * The verdict as both doors publish it (`GET /v1/fleet/utilization` `boxes[].verdict`,
 * MCP `box fleet_status` `boxes[].verdict`), snake_case like the rest of each row.
 */
export type MachineVerdictWire = {
  status: MachineStatus;
  health: MachineHealth;
  needs_attention: boolean;
  signals: MachineSignals;
  /** The worst of the account's jobs on the machine (#6746), null when none counts. */
  jobs_state: MachineJobsVerdictState | null;
};

export function machineVerdictWire(v: MachineVerdict): MachineVerdictWire {
  return {
    status: v.status,
    health: v.health,
    needs_attention: v.needsAttention,
    signals: v.signals,
    jobs_state: v.jobs,
  };
}
